import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActionTypes as UnityActionTypes, WorkoutUnityResult } from 'web_unity_engine';
import {
  IExerciseBalancingConfig,
  IInitUnityPayload,
  ILoadBalancingPayload,
  IPrepareExercisePayload,
  IStartExercisePayload,
} from './types';

export interface IUnityState {
  // engine started flag
  started: boolean;
  // loading progress
  progress: number;
  // current exercise name
  exerciseId: string;
  // current workout id
  workoutId: number;
  // exercise type (assessment or training) - needed for tracking
  exerciseType: 'training' | 'assessment';
  // running gameplay
  running: boolean;
  // assessment run number (only for assessment needed)
  assessmentRun: number;
  // current mode of the exercise (exercise/learning)
  mode?: 'exercise' | 'learning';
  // current exercise balancing
  balancing?: IExerciseBalancingConfig;
}

export const initialState: IUnityState = {
  started: false,
  progress: 0,
  exerciseId: '',
  workoutId: 0,
  exerciseType: 'training',
  running: false,
  assessmentRun: 0,
};

export const statePropName = 'unity';

const unitySlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    initUnity: {
      reducer: (state, action: PayloadAction<IInitUnityPayload>) => {
        state.exerciseType = action.payload.exerciseType;
        state.assessmentRun = action.payload.assessmentRun ?? 0;
      },
      prepare: (exerciseType: 'assessment' | 'training', assessmentRun?: number, basePath?: string) => ({
        payload: { exerciseType, assessmentRun, basePath },
      }),
    },
    unityStarted: (state) => {
      state.started = true;
    },
    unityRestart: (state) => {},
    unityRestarted: (state) => {},
    prepareExercise: {
      reducer: (state, action: PayloadAction<IPrepareExercisePayload>) => {
        state.exerciseId = action.payload.exerciseId;
        state.workoutId = action.payload.workoutId;
        state.running = true;
      },
      prepare: (exerciseId: string, workoutId: number) => ({
        payload: { exerciseId, workoutId },
      }),
    },
    exerciseReady: (state) => {
      // No state change needed for exercise ready
    },
    startExercise: {
      reducer: (state, action: PayloadAction<IStartExercisePayload>) => {
        state.mode = 'exercise';
      },
      /**
       * start exercise in workout mode
       * @param {number} difficulty difficulty level
       * @param {number} duration duration of the exercise from config
       * @param {number} goal score goal for the current workout
       * @param {number} exerciseRun counter for current exercise run in total for given user
       */
      prepare: (difficulty: number, duration: number, goal = 0, exerciseRun?: number) => ({
        payload: { difficulty, duration, goal, exerciseRun },
      }),
    },
    /**
     * start exercise in learning mode
     * @param {number} difficulty difficulty level
     */
    startLearning: (state, action: PayloadAction<number>) => {
      state.mode = 'learning';
    },
    exerciseComplete: (state, action: PayloadAction<WorkoutUnityResult>) => {
      state.running = false;
      state.mode = undefined;
    },
    balancing: (state, action: PayloadAction<IExerciseBalancingConfig | undefined>) => {
      state.balancing = action.payload;
    },
    loadBalancing: {
      reducer: (state, action: PayloadAction<ILoadBalancingPayload>) => {
        // clear balancing field when new balancing requested
        state.balancing = undefined;
      },
      prepare: (exerciseId: string, exerciseJsonFile: string) => ({
        payload: { exerciseId, exerciseJsonFile },
      }),
    },
  },
  extraReducers(builder) {
    return builder.addCase(
      UnityActionTypes.UNITY_PROGRESS_UPDATE,
      (state, action: UnityActionTypes.IUnityProgressUpdateAction) => {
        state.progress = action.payload;
      }
    );
  },
});

export const UnityActions = unitySlice.actions;
export default unitySlice.reducer;
